
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Briefcase, Settings } from 'lucide-react';

const Login = () => {
  const roleOptions = [
    {
      title: 'Patient',
      description: 'Book treatments, manage Studio Credits, and unlock exclusive rewards',
      icon: Users,
      href: '/login/patient',
      color: 'from-juv-soft-blush to-juv-cream-white',
      iconColor: 'text-juv-terracotta'
    },
    {
      title: 'Staff',
      description: 'Manage member check-ins, track patient credits, and access training tools',
      icon: Briefcase,
      href: '/login/staff',
      color: 'from-blue-50 to-slate-50',
      iconColor: 'text-blue-600'
    },
    {
      title: 'HQ',
      description: 'Access launch kits, system analytics, SOPs, and AI agent tools',
      icon: Settings,
      href: '/login/hq',
      color: 'from-slate-100 to-slate-200',
      iconColor: 'text-slate-700'
    }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-juv-cream-white to-juv-soft-blush">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-br from-juv-terracotta to-juv-deep-chestnut mb-6">
            <span className="text-white text-3xl font-bold font-juv-heading">J</span>
          </div>
          <h1 className="text-4xl font-bold font-juv-heading text-juv-deep-chestnut mb-4">
            Log In to Your JUV Dashboard
          </h1>
          <p className="text-lg text-juv-deep-chestnut/70 max-w-2xl mx-auto">
            Select your role to access your personalized tools and experience.
          </p>
        </div>

        {/* Role Selection Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {roleOptions.map((role) => {
            const Icon = role.icon;
            return (
              <Card 
                key={role.title}
                className={`relative overflow-hidden border-2 border-transparent hover:border-opacity-50 transition-all duration-300 hover:scale-105 bg-gradient-to-br ${role.color}`}
              >
                <CardHeader className="text-center pb-4">
                  <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-white/50 backdrop-blur-sm mb-4`}>
                    <Icon className={`w-8 h-8 ${role.iconColor}`} />
                  </div>
                  <CardTitle className="text-xl font-bold text-slate-800">
                    {role.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center pt-0">
                  <p className="text-sm text-slate-600 mb-6 min-h-[40px]">
                    {role.description}
                  </p>
                  <Button 
                    asChild
                    className="w-full bg-white/80 hover:bg-white text-slate-800 font-medium border border-slate-200"
                  >
                    <Link to={role.href}>
                      Log In as {role.title}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer Links */}
        <div className="text-center space-y-4">
          <div className="text-sm text-juv-deep-chestnut/70">
            Don't have an account? 
            <Link to="/auth" className="ml-1 font-medium text-juv-terracotta hover:opacity-70 transition-opacity">
              Join JUV
            </Link>
          </div>
          <div>
            <Link 
              to="/" 
              className="text-sm text-juv-deep-chestnut/60 hover:text-juv-terracotta transition-colors"
            >
              ← Back to JUV Website
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
